import { useEffect, useRef, useState } from 'react';

const DEFAULT_DELAY_MS = 200;
const DEFAULT_MIN_VISIBLE_MS = 450;

interface DelayedVisibleOptions {
  /** Wait this long before showing, so fast requests never flash an indicator. */
  delayMs?: number;
  /** Once shown, stay visible at least this long, so the indicator never strobes. */
  minVisibleMs?: number;
}

/**
 * Debounces a boolean "busy" signal into a boolean "show the indicator" signal.
 *
 * Sub-`delayMs` work stays invisible; anything slower shows an indicator that
 * lingers for `minVisibleMs` instead of blinking out.
 */
export function useDelayedVisible(active: boolean, options: DelayedVisibleOptions = {}): boolean {
  const { delayMs = DEFAULT_DELAY_MS, minVisibleMs = DEFAULT_MIN_VISIBLE_MS } = options;
  const [visible, setVisible] = useState(false);
  const shownAt = useRef<number | null>(null);

  useEffect(() => {
    if (active === visible) return;

    if (active) {
      const timer = window.setTimeout(() => {
        shownAt.current = Date.now();
        setVisible(true);
      }, delayMs);
      return () => window.clearTimeout(timer);
    }

    const elapsed = shownAt.current === null ? minVisibleMs : Date.now() - shownAt.current;
    const timer = window.setTimeout(
      () => {
        shownAt.current = null;
        setVisible(false);
      },
      Math.max(0, minVisibleMs - elapsed),
    );
    return () => window.clearTimeout(timer);
  }, [active, visible, delayMs, minVisibleMs]);

  return visible;
}
